import React from 'react';
import { Alert, Button, Col, Modal, Row, Space, Typography, message } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { StatCards, QueueList, JobList, JobDetail } from './components';
import { useQueueMonitor, QueueItem, JobItem } from './hooks/useQueueMonitor';

const { Title } = Typography;

const getErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || fallback;
};

type ViewLevel = 'queues' | 'jobs' | 'detail';

export const QueueMonitor: React.FC = () => {
  const {
    queues,
    jobs,
    loading,
    error,
    stats,
    tenants,
    meta,
    jobFilters,
    setJobFilters,
    setJobs,
    setMeta,
    refetchQueues,
    refetchJobs,
    cancelJob,
    retryJob,
  } = useQueueMonitor();

  const [viewLevel, setViewLevel] = React.useState<ViewLevel>('queues');
  const [selectedQueue, setSelectedQueue] = React.useState<QueueItem | null>(null);
  const [selectedJob, setSelectedJob] = React.useState<JobItem | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);

  const handleViewJobs = async (queue: QueueItem) => {
    setSelectedQueue(queue);
    setViewLevel('jobs');
    await refetchJobs(queue.id);
  };

  const handleBackToQueues = () => {
    setViewLevel('queues');
    setSelectedQueue(null);
    setJobs([]);
    setJobFilters({ page: 1, limit: 20 });
    setMeta({ page: 1, limit: 20, total: 0, totalPages: 0 });
  };

  const handleBackToJobs = () => {
    setViewLevel('jobs');
    setSelectedJob(null);
  };

  const handleFilterChange = (key: 'tenant_id' | 'status', value: string | undefined) => {
    setJobFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setJobFilters((prev) => ({ ...prev, page, limit: pageSize }));
  };

  const handleCancelJob = async (jobId: string) => {
    try {
      await cancelJob(jobId);
      message.success('Job cancelled successfully');
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to cancel job'));
    }
  };

  const handleRetryJob = async (jobId: string) => {
    try {
      await retryJob(jobId);
      message.success('Job requeued successfully');
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to retry job'));
    }
  };

  const handleViewDetail = async (job: JobItem) => {
    setSelectedJob(job);
    setDetailOpen(true);
    setViewLevel('detail');
  };

  const handleRefetch = async () => {
    if (viewLevel === 'queues') {
      await refetchQueues();
    } else if (viewLevel === 'jobs' && selectedQueue) {
      await refetchJobs(selectedQueue.id);
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            {viewLevel !== 'queues' && (
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={viewLevel === 'detail' ? handleBackToJobs : handleBackToQueues}
              >
                Back
              </Button>
            )}
            <Title level={3} style={{ margin: 0 }}>
              {viewLevel === 'queues' ? 'Queue Monitor' : selectedQueue?.name || 'Jobs'}
            </Title>
          </Space>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={handleRefetch}>Refresh</Button>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load data"
          description={error.message}
          action={<Button size="small" onClick={handleRefetch}>Retry</Button>}
        />
      )}

      {viewLevel === 'queues' && (
        <>
          <StatCards stats={stats} />
          <QueueList
            queues={queues}
            loading={loading}
            onRefresh={refetchQueues}
            onViewJobs={handleViewJobs}
            pagination={{
              current: meta.page,
              pageSize: meta.limit,
              total: meta.total,
              onChange: (page, pageSize) => setMeta((prev) => ({ ...prev, page, limit: pageSize })),
            }}
          />
        </>
      )}

      {viewLevel === 'jobs' && selectedQueue && (
        <JobList
          jobs={jobs}
          loading={loading}
          onRefresh={() => refetchJobs(selectedQueue.id)}
          onViewDetail={handleViewDetail}
          onCancelJob={handleCancelJob}
          onRetryJob={handleRetryJob}
          tenants={tenants}
          pagination={{
            current: meta.page,
            pageSize: meta.limit,
            total: meta.total,
            onChange: handlePageChange,
          }}
          filters={jobFilters}
          onFilterChange={handleFilterChange}
          queueName={selectedQueue.name}
        />
      )}

      <Modal
        title="Job Detail"
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setViewLevel('jobs');
        }}
        footer={null}
        width={600}
      >
        <JobDetail job={selectedJob} />
      </Modal>
    </div>
  );
};

export default QueueMonitor;